import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import { useCustomers, useDeleteCustomer } from "../hooks/use-customers";
import type { CustomerWithEmails } from "../types/customer.types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowUpDown, MoreHorizontal, Trash2, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";

export function CustomerList() {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data: customers, isLoading } = useCustomers();
  const navigate = useNavigate();
  const deleteCustomer = useDeleteCustomer();

  const handleDelete = (id: string, name: string) => {
    setCustomerToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;

    try {
      await deleteCustomer.mutateAsync(customerToDelete.id);
      console.log("✅ Customer deleted successfully:", customerToDelete.id);
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    } catch (error) {
      console.error("❌ Delete failed:", error);
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const handleView = (id: string) => {
    navigate({ to: `/dashboard/customers/edit/${id}` });
  };

  const getCustomerName = (customer: CustomerWithEmails): string => {
    if (customer.customerType === "business") {
      return customer.companyName || "-";
    }
    return (
      `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "-"
    );
  };

  const getPrimaryEmail = (customer: CustomerWithEmails): string => {
    const primary = customer.emails.find(
      (e: { email: string; isPrimary: string }) => e.isPrimary === "true",
    );
    return primary?.email || customer.emails[0]?.email || "-";
  };

  const columns: ColumnDef<CustomerWithEmails>[] = [
    {
      accessorKey: "customerNumber",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-1 text-xs sm:px-2 sm:text-sm"
          >
            <span className="hidden sm:inline">
              {t("customers.columns.customerNumber")}
            </span>
            <span className="sm:hidden">Nr.</span>
            <ArrowUpDown className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div
          className="text-primary cursor-pointer text-xs font-medium hover:underline sm:text-sm"
          onClick={() =>
            navigate({ to: `/dashboard/customers/${row.original.id}` })
          }
        >
          {row.getValue("customerNumber")}
        </div>
      ),
    },
    {
      id: "name",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-1 text-xs sm:px-2 sm:text-sm"
          >
            {t("customers.columns.name")}
            <ArrowUpDown className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        );
      },
      accessorFn: (row) => getCustomerName(row),
      cell: ({ row }) => (
        <div
          className="text-primary cursor-pointer text-xs hover:underline sm:text-sm"
          onClick={() =>
            navigate({ to: `/dashboard/customers/${row.original.id}` })
          }
        >
          {getCustomerName(row.original)}
        </div>
      ),
    },
    {
      accessorKey: "customerType",
      header: () => (
        <div className="hidden text-xs sm:text-sm md:table-cell">
          {t("customers.columns.type")}
        </div>
      ),
      cell: ({ row }) => {
        const type = row.getValue("customerType") as "business" | "private";
        return (
          <div className="hidden md:table-cell">
            <Badge
              variant={type === "business" ? "default" : "secondary"}
              className="text-[10px] sm:text-xs"
            >
              {t(`customers.types.${type}`)}
            </Badge>
          </div>
        );
      },
    },
    {
      id: "email",
      header: () => (
        <div className="hidden text-xs sm:text-sm lg:table-cell">
          {t("customers.columns.email")}
        </div>
      ),
      accessorFn: (row) => getPrimaryEmail(row),
      cell: ({ row }) => (
        <div className="hidden text-xs sm:text-sm lg:table-cell">
          {getPrimaryEmail(row.original)}
        </div>
      ),
    },
    {
      accessorKey: "phone",
      header: () => (
        <div className="hidden text-xs sm:text-sm lg:table-cell">
          {t("customers.columns.phone")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="hidden text-xs sm:text-sm lg:table-cell">
          {row.getValue("phone") || "-"}
        </div>
      ),
    },
    {
      accessorKey: "billingCity",
      header: () => (
        <div className="hidden text-xs sm:text-sm xl:table-cell">
          {t("customers.columns.city")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="hidden text-xs sm:text-sm xl:table-cell">
          {row.getValue("billingCity") || "-"}
        </div>
      ),
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const customer = row.original;
        const customerName = getCustomerName(customer);

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">
                  {t("customers.actions.openMenu")}
                </span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                {t("customers.actions.menuLabel")}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(customer.id);
                }}
              >
                {t("customers.actions.copyId")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(customer.id);
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                {t("customers.actions.edit")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(customer.id, customerName);
                }}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("customers.actions.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: customers || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
  });

  if (isLoading) {
    return <div className="p-8 text-center">{t("customers.loading")}</div>;
  }

  return (
    <div className="w-full max-w-full space-y-4 overflow-hidden">
      <div className="flex items-center">
        <Input
          placeholder={t("customers.searchPlaceholder")}
          value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("name")?.setFilterValue(event.target.value)
          }
          className="w-full sm:max-w-sm"
        />
      </div>

      {/* Mobile Card View */}
      <div className="space-y-3 sm:hidden">
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => {
            const customer = row.original;
            const type = customer.customerType as "business" | "private";
            return (
              <div
                key={row.id}
                onClick={() => handleView(customer.id)}
                className="bg-card hover:bg-muted/50 cursor-pointer space-y-2 rounded-lg border p-3"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <Badge
                      variant={type === "business" ? "default" : "secondary"}
                      className="text-[10px]"
                    >
                      {t(`customers.types.${type}`)}
                    </Badge>
                    <div className="text-primary text-sm font-medium">
                      {getCustomerName(customer)}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {customer.customerNumber}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>
                        {t("customers.actions")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(customer.id);
                        }}
                      >
                        {t("customers.copyId")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(customer.id);
                        }}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        {t("customers.view")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(customer.id, getCustomerName(customer));
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t("customers.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="text-muted-foreground grid grid-cols-1 gap-1 text-xs">
                  <div>{getPrimaryEmail(customer)}</div>
                  {customer.phone && <div>{customer.phone}</div>}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-muted-foreground py-8 text-center">
            {t("customers.noCustomers")}
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden overflow-hidden rounded-md border sm:block">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const columnId = header.column.id;
                    const hiddenClass =
                      columnId === "customerType"
                        ? "hidden md:table-cell"
                        : columnId === "email" || columnId === "phone"
                          ? "hidden lg:table-cell"
                          : columnId === "billingCity"
                            ? "hidden xl:table-cell"
                            : "";
                    return (
                      <TableHead
                        key={header.id}
                        className={`px-2 py-2 sm:px-4 sm:py-3 ${hiddenClass}`}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => handleView(row.original.id)}
                    className="hover:bg-muted/50 cursor-pointer"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const columnId = cell.column.id;
                      const hiddenClass =
                        columnId === "customerType"
                          ? "hidden md:table-cell"
                          : columnId === "email" || columnId === "phone"
                            ? "hidden lg:table-cell"
                            : columnId === "billingCity"
                              ? "hidden xl:table-cell"
                              : "";
                      return (
                        <TableCell
                          key={cell.id}
                          className={`px-1 py-2 sm:px-4 sm:py-3 ${hiddenClass}`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {t("customers.noCustomers")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-muted-foreground text-xs sm:text-sm">
          {customers &&
            t("customers.pagination.page", {
              current: pagination.pageIndex + 1,
              total: Math.ceil(customers.length / pagination.pageSize),
              count: customers.length,
            })}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="flex-1 sm:flex-initial"
          >
            {t("customers.pagination.previous")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="flex-1 sm:flex-initial"
          >
            {t("customers.pagination.next")}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("customers.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {customerToDelete &&
                t("customers.deleteConfirm", { name: customerToDelete.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCustomerToDelete(null)}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("customers.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
